import type { Character, Placement, Zone } from "@cloudnest/horror-race-engine";
import { useEffect, useMemo, useState } from "react";

/**
 * T23-b — the active player's in-progress coin placement, lifted out of CoinHand
 * so it can feed BOTH the bottom chip tray (CoinHand) and the top ZoneBoard
 * (which shows a drawer-only preview and is the drop target). Client-only: the
 * server still sees nothing until 배치 확정 commits via onPlace.
 *
 * Interaction: pick up a coin (pickCoin) → ZoneBoard zones light up → tap a zone
 * (placeActiveInZone) → the coin is temp-placed there and the coin deactivates.
 * A placed coin can be re-picked (toggles active) to move it; 취소 deactivates;
 * 초기화 clears every temp placement.
 */
export interface PlacementDraft {
  /** Temp zone per drawn-coin index (null = not yet placed). */
  readonly assignments: Readonly<Record<number, Zone | null>>;
  /** The coin currently "picked up", or null. */
  readonly activeCoinIndex: number | null;
  /** Every drawn coin has a zone — 배치 확정 may enable. */
  readonly allAssigned: boolean;
  /** At least one coin temp-placed — 초기화 may enable. */
  readonly anyPlaced: boolean;
  /** Count of coins temp-placed (for status copy). */
  readonly placedCount: number;
  /** Drawer-only preview: temp additions per zone per character. */
  readonly pendingByZone: Readonly<
    Record<Zone, Readonly<Partial<Record<Character, number>>>>
  >;
  /** Zone the active coin already sits in (excluded from the drop highlight). */
  readonly activeCoinZone: Zone | null;
  /** Pick up / toggle-off coin `index`. */
  readonly pickCoin: (index: number) => void;
  /** Drop the active coin into `zone` (no-op if nothing active). */
  readonly placeActiveInZone: (zone: Zone) => void;
  /** Deactivate the active coin (no placement change). */
  readonly cancelActive: () => void;
  /** Clear all temp placements + deactivate. */
  readonly reset: () => void;
  /** Built placements when all coins are assigned, else null. */
  readonly buildPlacements: () => readonly Placement[] | null;
}

function emptyAssignments(count: number): Record<number, Zone | null> {
  return Object.fromEntries(Array.from({ length: count }, (_, i) => [i, null]));
}

export function usePlacementDraft(
  coins: readonly Character[],
  turnSig: string,
): PlacementDraft {
  const [assignments, setAssignments] = useState<Record<number, Zone | null>>(
    () => emptyAssignments(coins.length),
  );
  const [activeCoinIndex, setActiveCoinIndex] = useState<number | null>(null);

  // Reset on a NEW draw — the turn point (turnSig) changes, or the drawn-coin
  // count changes (pool exhaustion). Mirrors the old CoinHand reset-on-length.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on turnSig + count, not the live array
  useEffect(() => {
    setAssignments(emptyAssignments(coins.length));
    setActiveCoinIndex(null);
  }, [turnSig, coins.length]);

  const slots = Array.from({ length: coins.length }, (_, i) => assignments[i]);
  const allAssigned =
    coins.length > 0 && slots.every((z) => z !== null && z !== undefined);
  const placedCount = slots.filter((z) => z !== null && z !== undefined).length;
  const anyPlaced = placedCount > 0;
  const activeCoinZone =
    activeCoinIndex !== null ? (assignments[activeCoinIndex] ?? null) : null;

  const pendingByZone = useMemo(() => {
    const acc: Record<Zone, Partial<Record<Character, number>>> = {
      1: {},
      2: {},
      4: {},
    };
    for (let i = 0; i < coins.length; i += 1) {
      const zone = assignments[i];
      if (zone === null || zone === undefined) continue;
      const character = coins[i];
      acc[zone][character] = (acc[zone][character] ?? 0) + 1;
    }
    return acc;
  }, [assignments, coins]);

  const pickCoin = (index: number) =>
    setActiveCoinIndex((prev) => (prev === index ? null : index));

  const placeActiveInZone = (zone: Zone) => {
    if (activeCoinIndex === null) return;
    setAssignments((prev) => ({ ...prev, [activeCoinIndex]: zone }));
    setActiveCoinIndex(null);
  };

  const cancelActive = () => setActiveCoinIndex(null);

  const reset = () => {
    setAssignments(emptyAssignments(coins.length));
    setActiveCoinIndex(null);
  };

  const buildPlacements = (): readonly Placement[] | null => {
    if (!allAssigned) return null;
    return coins.map((character, i) => ({
      character,
      zone: assignments[i] as Zone,
    }));
  };

  return {
    assignments,
    activeCoinIndex,
    allAssigned,
    anyPlaced,
    placedCount,
    pendingByZone,
    activeCoinZone,
    pickCoin,
    placeActiveInZone,
    cancelActive,
    reset,
    buildPlacements,
  };
}
